import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

/**
 * usePAICStore
 *
 * Zustand store for People+AI Collaboration (PAIC) state management.
 * Replaces PAICProjectContext / Redux slices for better performance
 * and fine-grained subscriptions.
 */
const initialProjectState = {
  projectId: null,
  groupName: "",
  projectSource: null, // "owned" | "delegated" | "shared" — drives read-only guards
  loading: false,

  agentId: null,
  selectionId: null,
  selectedVersionNumber: null,

  roleSBUniqueId: null,
  rolePositionCount: null,
  roleTotalComp: null,

  viewByToggle: false,
};

const initialJdPrdViewState = {
  documentType: "agent-prd",
  viewMode: "VIEW",
  selectedDocumentAgentId: null,
  selectedDocumentVersion: "jd-version-1",
  jdPrdLoading: false,

  // Selected JD version with its linked agents, plus that version's PRD/JD data
  selectedVersionWithAgents: null,
  selectedVersionPrdData: {},
  selectedVersionJdData: {},
};

const usePAICStore = create(
  devtools(
    persist(
      (set) => ({
        // Project / selection / role state
        ...initialProjectState,

        // JD / PRD View State
        ...initialJdPrdViewState,

        // Actions
        setProjectId: (id) =>
          set({ projectId: id }, false, "setProjectId"),

        setGroupName: (name) =>
          set({ groupName: name }, false, "setGroupName"),

        setProjectSource: (source) =>
          set({ projectSource: source }, false, "setProjectSource"),

        setLoading: (loading) =>
          set({ loading }, false, "setLoading"),

        setAgentId: (id) =>
          set({ agentId: id }, false, "setAgentId"),

        setSelectionId: (id) =>
          set({ selectionId: id }, false, "setSelectionId"),

        setSelectedVersionNumber: (version) =>
          set(
            { selectedVersionNumber: version },
            false,
            "setSelectedVersionNumber",
          ),

        setRoleSBUniqueId: (id) =>
          set({ roleSBUniqueId: id }, false, "setRoleSBUniqueId"),

        setRolePositionCount: (count) =>
          set({ rolePositionCount: count }, false, "setRolePositionCount"),

        setRoleTotalComp: (comp) =>
          set({ roleTotalComp: comp }, false, "setRoleTotalComp"),

        setViewByToggle: (toggle) =>
          set({ viewByToggle: toggle }, false, "setViewByToggle"),

        setDocumentType: (type) =>
          set({ documentType: type }, false, "setDocumentType"),

        setViewMode: (mode) =>
          set({ viewMode: mode }, false, "setViewMode"),

        setSelectedDocumentAgentId: (id) =>
          set(
            { selectedDocumentAgentId: id },
            false,
            "setSelectedDocumentAgentId",
          ),

        setSelectedDocumentVersion: (version) =>
          set(
            { selectedDocumentVersion: version },
            false,
            "setSelectedDocumentVersion",
          ),

        setJdPrdLoading: (loading) =>
          set({ jdPrdLoading: loading }, false, "setJdPrdLoading"),

        // JD / PRD data actions
        setSelectedVersionWithAgents: (data) =>
          set(
            { selectedVersionWithAgents: data },
            false,
            "setSelectedVersionWithAgents",
          ),

        setSelectedVersionPrdData: (data) =>
          set(
            { selectedVersionPrdData: data || {} },
            false,
            "setSelectedVersionPrdData",
          ),

        setSelectedVersionJdData: (data) =>
          set(
            { selectedVersionJdData: data || {} },
            false,
            "setSelectedVersionJdData",
          ),

        clearSelectedVersionPrdData: () =>
          set(
            { selectedVersionPrdData: {} },
            false,
            "clearSelectedVersionPrdData",
          ),

        clearSelectedVersionJdData: () =>
          set(
            { selectedVersionJdData: {} },
            false,
            "clearSelectedVersionJdData",
          ),

        // Compound Actions
        resetProject: () =>
          set(
            {
              ...initialProjectState,
            },
            false,
            "resetProject",
          ),

        resetJdPrdView: () =>
          set(
            {
              ...initialJdPrdViewState,
            },
            false,
            "resetJdPrdView",
          ),

        resetAllPAICState: () =>
          set(
            {
              ...initialProjectState,
              ...initialJdPrdViewState,
            },
            false,
            "resetAllPAICState",
          ),
      }),
      {
        name: "paic-store",
        partialize: (state) => ({
          projectId: state.projectId,
          groupName: state.groupName,
          projectSource: state.projectSource,
          agentId: state.agentId,
          selectionId: state.selectionId,
          selectedVersionNumber: state.selectedVersionNumber,
          roleSBUniqueId: state.roleSBUniqueId,
          rolePositionCount: state.rolePositionCount,
          roleTotalComp: state.roleTotalComp,
          viewByToggle: state.viewByToggle,
          documentType: state.documentType,
          viewMode: state.viewMode,
          selectedDocumentAgentId: state.selectedDocumentAgentId,
          selectedDocumentVersion: state.selectedDocumentVersion,
          selectedVersionWithAgents: state.selectedVersionWithAgents,
        }),
      },
    ),
    { name: "PAIC Store", anonymousActionType: "paic" },
  ),
);

export default usePAICStore;